import { create } from 'zustand'
import { getAxiosInstance } from '@/network/axiosInstance'
import { axiosRequest } from '../network/axiosInstance'

// Agent types
export interface Agent {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  projectId: string
  enabled: boolean
  avatarUrl?: string
  role?: string
  type: string
  instructions?: string
  triggerEvents: string[]
  observationList: string[]
  settings?: any
  tasks?: AgentTask[]
  logs?: AgentLog[]
}

export interface AgentTask {
  id: string
  createdAt: string
  updatedAt: string
  agentId: string
  taskType: string
  scheduleType?: string
  startDate?: string
  endDate?: string
  frequency?: string
  maxRuns?: number
  currentRuns: number
  lastRun?: string
  nextRun?: string
  instructions: string
  targetNodeIds: string[]
  status: string
  settings?: any
}

export interface AgentLog {
  id: string
  createdAt: string
  agentId: string
  taskId?: string
  action: string
  triggerEvent?: string
  targetNodeId?: string
  result: string
  details?: string
  reasoning?: string
  expectedOutcome?: string
  actualOutcome?: string
  score?: number
  executionTime?: number
  metadata?: any
}

// Store state interface
export interface AgentsState {
  agentsLoading: boolean
  agentsFetchSuccess: boolean
  agentsFetchError: boolean
  agents: Agent[] | null
  agentsErrorMessage: string | null
  fetchAgents: (projectId: string) => Promise<void>
}

// Create a Zustand store for fetching agents
export const useGetAgents = create<AgentsState>((set) => ({
  agentsLoading: false,
  agentsFetchSuccess: false,
  agentsFetchError: false,
  agents: null,
  agentsErrorMessage: null,
  fetchAgents: async (projectId: string) => {
    set({ agentsLoading: true, agentsFetchError: false, agentsErrorMessage: null })
    try {
      const response = await fetch(`/api/projects/${projectId}/agents`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const agents = await response.json()
      set({
        agents,
        agentsLoading: false,
        agentsFetchSuccess: true,
        agentsFetchError: false,
        agentsErrorMessage: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch agents'
      set({
        agents: null,
        agentsLoading: false,
        agentsFetchSuccess: false,
        agentsFetchError: true,
        agentsErrorMessage: errorMessage
      })
    }
  }
}))

// Create mutation-style hooks that work like axiosRequest but handle data properly
export const useCreateAgent = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const response = await getAxiosInstance().post(`/api/projects/${data.projectId}/agents`, data)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

export const useUpdateAgent = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const { projectId, agentId, ...updateData } = data
        const response = await getAxiosInstance().put(`/api/projects/${projectId}/agents/${agentId}`, updateData)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

export const useDeleteAgent = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const { projectId, agentId } = data
        const response = await getAxiosInstance().delete(`/api/projects/${projectId}/agents/${agentId}`)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

export const useGetAgentLogs = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const { projectId, agentId } = data
        const response = await getAxiosInstance().get(`/api/projects/${projectId}/agents/${agentId}/logs`)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

export const useGetAgentTasks = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const { projectId, agentId } = data
        const response = await getAxiosInstance().get(`/api/projects/${projectId}/agents/${agentId}/tasks`)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

export const useCreateAgentTask = () => {
  return {
    loading: false,
    execute: async (data: any, options?: { onSuccess?: (result: any) => void; onError?: (error: any) => void }) => {
      try {
        const { projectId, agentId, ...taskData } = data
        const response = await getAxiosInstance().post(`/api/projects/${projectId}/agents/${agentId}/tasks`, taskData)
        options?.onSuccess?.(response.data)
        return response.data
      } catch (error) {
        options?.onError?.(error)
        throw error
      }
    }
  }
}

// Get agent by ID
export const useGetAgent = axiosRequest({
  method: 'GET',
  url: '/api/projects/:projectId/agents/:agentId',
})

// Update agent observation list
export const useUpdateAgentObservation = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:projectId/agents/:agentId/observation',
})

// Update agent task
export const useUpdateAgentTask = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:projectId/agents/:agentId/tasks/:taskId',
})

// Delete agent task
export const useDeleteAgentTask = axiosRequest({
  method: 'DELETE',
  url: '/api/projects/:projectId/agents/:agentId/tasks/:taskId',
})

// Create agent log
export const useCreateAgentLog = axiosRequest({
  method: 'POST',
  url: '/api/projects/:projectId/agents/:agentId/logs',
})

// Get enabled agents (internal)
export const useGetEnabledAgents = axiosRequest({
  method: 'GET',
  url: '/api/projects/:projectId/agents/enabled',
})

// Get agents by trigger
export const useGetAgentsByTrigger = axiosRequest({
  method: 'GET',
  url: '/api/projects/:projectId/agents/trigger',
})
